import * as Yup from "yup";
import { SellingMode } from "../enums/sellingMode";

/** Max items to create in one go to avoid memory overload */
export const MAX_ITEMS_PER_BATCH = 50;

export interface BatchQuantityFormData {
  quantity: number;
}

export interface AddItemsInStockFormData extends BatchQuantityFormData {
  totalQuantity?: number;
}

/** Shared Yup field for batch item count */
const batchQuantityField = (t?: (key: string, fallback: string) => string) => {
  const tFn = t ?? ((_key: string, fallback: string) => fallback);
  return Yup.number()
    .required(
      tFn("add-items-in-stock-quantity-required", "Quantity is required")
    )
    .min(
      1,
      tFn("add-items-in-stock-quantity-min", "Quantity must be at least 1")
    )
    .max(
      MAX_ITEMS_PER_BATCH,
      tFn(
        "add-items-in-stock-quantity-max",
        "Maximum {{max}} items at once to avoid overloading",
        { max: MAX_ITEMS_PER_BATCH }
      )
    )
    .integer(
      tFn(
        "add-items-in-stock-quantity-integer",
        "Quantity must be a whole number"
      )
    );
};

export const getBatchQuantitySchema = (
  t?: (key: string, fallback: string) => string
) => {
  return Yup.object().shape({
    quantity: batchQuantityField(t)
  }) as Yup.ObjectSchema<BatchQuantityFormData>;
};

export const getAddItemsInStockSchema = (
  sellingMode?: SellingMode,
  t?: (key: string, fallback: string) => string
) => {
  const tFn = t ?? ((_key: string, fallback: string) => fallback);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const baseSchema: Record<string, any> = {
    quantity: batchQuantityField(t)
  };

  if (sellingMode === SellingMode.UNIT_BASED) {
    baseSchema.totalQuantity = Yup.number()
      .required(
        tFn(
          "add-items-in-stock-total-quantity-required",
          "Total quantity is required"
        )
      )
      .min(
        1,
        tFn(
          "add-items-in-stock-total-quantity-min",
          "Total quantity must be at least 1"
        )
      )
      .integer(
        tFn(
          "add-items-in-stock-total-quantity-integer",
          "Total quantity must be a whole number"
        )
      );
  } else if (sellingMode === SellingMode.MEASURED) {
    baseSchema.totalQuantity = Yup.number()
      .required(
        tFn(
          "add-items-in-stock-total-quantity-required",
          "Total quantity is required"
        )
      )
      .moreThan(
        0,
        tFn(
          "add-items-in-stock-measured-total-positive",
          "Total quantity must be greater than 0"
        )
      );
  }

  return Yup.object().shape(
    baseSchema
  ) as Yup.ObjectSchema<AddItemsInStockFormData>;
};
